// web/src/api/admin.ts
//
// Typed fetch wrappers for /api/admin/* (Plans 11-04a/04b/05). Mirrors
// the existing api/turn.ts CSRF-aware fetch pattern (Phase-1 D-14
// double-submit). Every state-changing call (POST/PATCH/PUT/DELETE) reads
// the csrf_token cookie via _csrf.ts::readCSRFToken and sends it back as
// the X-CSRF-Token header. The cookie is non-HttpOnly intentionally so
// JS can read it; this is the documented pattern.
//
// AdminError carries the HTTP status + raw body so screens can:
//   - Render 401/403 → re-auth prompt
//   - Render 422 publish-error envelope (PublishError) inline per slot
//   - Render 409 duplicate_short_code / has_children messages
//   - Surface 500/502 generically without leaking stack traces
//
// IMPORTANT: do NOT set Content-Type on the upload-image fetch — the
// browser must add the multipart boundary itself. Setting Content-Type
// would corrupt the request and the server would 400.

import { withCSRF, readCSRFToken } from "./_csrf.js";
import type {
  AdminImageUploadResponse,
  AdminSTTPresetDTO,
  AdminVoiceDTO,
  AdminVoiceListDTO,
  AdminVoiceCreateDTO,
  AdminTTSPhraseListDTO,
  AdminBulkRebuildResultDTO,
  AdminTTSCoverageDTO,
  AdminTTSCallListDTO,
  AdminTTSCostSummaryDTO,
  AdminPhraseOverrideDTO,
  AdminPhraseOverrideUpsertDTO,
} from "../types/api.js";

export class AdminError extends Error {
  status: number;
  bodyText: string;

  constructor(status: number, bodyText: string, message?: string) {
    super(message ?? `admin api ${status}`);
    this.name = "AdminError";
    this.status = status;
    this.bodyText = bodyText;
  }

  // Best-effort JSON parse of the response body. Returns undefined for
  // non-JSON bodies (e.g. plain-text 500 dumps).
  jsonBody<T>(): T | undefined {
    const trimmed = this.bodyText.trim();
    if (!trimmed) return undefined;
    try {
      return JSON.parse(trimmed) as T;
    } catch {
      return undefined;
    }
  }
}

async function readBodyText(res: Response): Promise<string> {
  try { return await res.text(); } catch { return ""; }
}

export async function adminGet<T>(path: string): Promise<T> {
  const r = await fetch(path, { credentials: "include" });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  return r.json() as Promise<T>;
}

export async function adminPost<TIn, TOut>(path: string, body: TIn): Promise<TOut> {
  const r = await fetch(path, {
    method: "POST",
    headers: withCSRF({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  // 204 No Content guards: callers that expect void may pass `unknown` and
  // ignore the result. Most admin endpoints return JSON (the row), but
  // /publish returns 200 + JSON; /delete returns 204 (handled by adminDelete).
  return r.json() as Promise<TOut>;
}

export async function adminPatch<TIn, TOut>(path: string, body: TIn): Promise<TOut> {
  const r = await fetch(path, {
    method: "PATCH",
    headers: withCSRF({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  return r.json() as Promise<TOut>;
}

export async function adminPut<TIn, TOut>(path: string, body: TIn): Promise<TOut> {
  const r = await fetch(path, {
    method: "PUT",
    headers: withCSRF({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  return r.json() as Promise<TOut>;
}

export async function adminDelete(path: string): Promise<void> {
  const r = await fetch(path, {
    method: "DELETE",
    headers: withCSRF(),
    credentials: "include",
  });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  // Server returns 204 No Content on success; nothing to parse.
}

// ─── Admin TTS console wrappers ───────────────────────────────────────────
// Thin typed shims over the generic verbs above. Mirror internal/admin/tts*.go.

export function getSTTPresets(): Promise<AdminSTTPresetDTO[]> {
  return adminGet<AdminSTTPresetDTO[]>("/api/admin/stt/presets");
}

export function getTTSVoices(): Promise<AdminVoiceListDTO> {
  return adminGet<AdminVoiceListDTO>("/api/admin/tts/voices");
}
export function createTTSVoice(body: AdminVoiceCreateDTO): Promise<AdminVoiceDTO> {
  return adminPost<AdminVoiceCreateDTO, AdminVoiceDTO>("/api/admin/tts/voices", body);
}
export function deleteTTSVoice(id: string): Promise<void> {
  return adminDelete(`/api/admin/tts/voices/${encodeURIComponent(id)}`);
}
export function getTTSPhrases(voiceID: string, page: number): Promise<AdminTTSPhraseListDTO> {
  const qs = new URLSearchParams();
  if (voiceID) qs.set("voice", voiceID);
  if (page > 1) qs.set("page", String(page));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminGet<AdminTTSPhraseListDTO>(`/api/admin/tts/phrases${suffix}`);
}
export function rebuildTTSBulk(
  voiceID: string,
  mode: "missing" | "force",
  scope: "voice" | "all",
): Promise<AdminBulkRebuildResultDTO> {
  return adminPost<{ voice: string; mode: "missing" | "force"; scope: "voice" | "all" }, AdminBulkRebuildResultDTO>(
    "/api/admin/tts/rebuild", { voice: voiceID, mode, scope },
  );
}
export function getTTSCoverage(): Promise<AdminTTSCoverageDTO> {
  return adminGet<AdminTTSCoverageDTO>("/api/admin/tts/coverage");
}
export function getTTSCosts(): Promise<AdminTTSCostSummaryDTO> {
  return adminGet<AdminTTSCostSummaryDTO>("/api/admin/tts/cost-summary");
}
export function getTTSCalls(page: number): Promise<AdminTTSCallListDTO> {
  const suffix = page > 1 ? `?page=${page}` : "";
  return adminGet<AdminTTSCallListDTO>(`/api/admin/tts/calls${suffix}`);
}
export function setTTSOverride(body: AdminPhraseOverrideUpsertDTO): Promise<AdminPhraseOverrideDTO | { deleted: true }> {
  return adminPut<AdminPhraseOverrideUpsertDTO, AdminPhraseOverrideDTO | { deleted: true }>("/api/admin/tts/overrides", body);
}
export function deleteTTSOverride(voiceID: string, text: string): Promise<void> {
  const qs = new URLSearchParams({ voice: voiceID, text });
  return adminDelete(`/api/admin/tts/overrides?${qs.toString()}`);
}
// updatePhraseContent rewrites the content of every in-use text row sharing
// one phrase entry in the admin /admin/tts phrase browser. The caller passes
// the `text_ids` it received in the listing DTO. The server does NOT auto-
// rebuild audio — the new content has a fresh SHA, so the row's audio_status
// flips to "missing" on reload and the existing Rebuild button handles render.
export function updatePhraseContent(
  textIDs: string[],
  newContent: string,
): Promise<{ updated: number }> {
  return adminPatch<{ text_ids: string[]; new_content: string }, { updated: number }>(
    "/api/admin/tts/phrases",
    { text_ids: textIDs, new_content: newContent },
  );
}

// rebuildTextAudio re-renders the text's clip for a voice. voiceID defaults
// to the config-active voice; force re-renders even if the file already
// exists (needed after editing a phrase override). Returns the
// {audio_status:"exists"} echo on success.
export function rebuildTextAudio(
  textID: string,
  voiceID?: string,
  force?: boolean,
): Promise<{ audio_status: string }> {
  const qs = new URLSearchParams();
  if (voiceID) qs.set("voice", voiceID);
  if (force) qs.set("force", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminPost<Record<string, never>, { audio_status: string }>(
    `/api/admin/texts/${encodeURIComponent(textID)}/rebuild-audio${suffix}`,
    {},
  );
}

// adminImportContentBundle: POST a zip (xlsx + images/**) as multipart.
// Returns the JSON {images_extracted, xlsx_bytes, replace, purge_sessions} on success.
// Throws AdminError on non-2xx (the body usually carries APIError shape).
export async function adminImportContentBundle(
  file: File,
  opts: { replace?: boolean; skipAudio?: boolean; purgeSessions?: boolean } = {},
): Promise<{ images_extracted: number; xlsx_bytes: number; replace: boolean; purge_sessions: boolean }> {
  const fd = new FormData();
  fd.append("bundle", file);
  const qs = new URLSearchParams();
  if (opts.replace) qs.set("replace", "true");
  if (opts.skipAudio) qs.set("skip_audio", "true");
  if (opts.purgeSessions) qs.set("purge_sessions", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await fetch(`/api/admin/content/import${suffix}`, {
    method: "POST",
    headers: { "X-CSRF-Token": readCSRFToken() },
    credentials: "include",
    body: fd,
  });
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  return r.json() as Promise<{ images_extracted: number; xlsx_bytes: number; replace: boolean; purge_sessions: boolean }>;
}

// adminUploadImage: multipart POST. Do NOT set Content-Type — the browser
// adds the multipart boundary. The CSRF token still rides as a header
// (the double-submit invariant from _csrf.ts).
export async function adminUploadImage(
  cardID: string,
  file: File,
): Promise<AdminImageUploadResponse> {
  const fd = new FormData();
  fd.append("image", file);
  const r = await fetch(
    `/api/admin/cards/${encodeURIComponent(cardID)}/upload-image`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": readCSRFToken() },
      credentials: "include",
      body: fd,
    },
  );
  if (!r.ok) throw new AdminError(r.status, await readBodyText(r));
  return r.json() as Promise<AdminImageUploadResponse>;
}
